import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, formatApiError } from "../lib/auth";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ArrowLeft, HeartHandshake, ShieldCheck, Lock } from "lucide-react";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/d4897236-428b-4abf-b3c0-e7679c5a4973/images/17dacbd358c08b08a71c9b212586df1c0a023f3cb13620d360997562ecb866ab.png";

export default function AuthPage() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({
    email: "",
    password: "",
    name: "",
    phone: "",
    role: "client",
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const goToPortal = (user) => {
    if (user.role === "admin") navigate("/admin");
    else if (user.role === "caregiver") navigate("/caregiver");
    else navigate("/client");
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        const u = await login(form.email, form.password);
        toast.success(`Welcome back, ${u.name.split(" ")[0]}.`);
        goToPortal(u);
      } else {
        if (!form.name || !form.email || !form.password) {
          toast.error("Please complete all required fields.");
          setLoading(false);
          return;
        }
        if (form.password.length < 6) {
          toast.error("Password must be at least 6 characters.");
          setLoading(false);
          return;
        }
        const u = await register({
          email: form.email,
          password: form.password,
          name: form.name,
          phone: form.phone,
          role: form.role,
        });
        toast.success(
          form.role === "caregiver"
            ? "Account created. An admin will approve you shortly."
            : "Account created. Let's set up care."
        );
        goToPortal(u);
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="auth-page" className="min-h-screen lg:grid lg:grid-cols-2 bg-[#FFFFFF]">
      {/* Visual panel */}
      <div className="relative hidden lg:block">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#005165]/95 via-[#005165]/70 to-[#005165]/40" />
        <div className="relative h-full flex flex-col justify-between p-12 text-[#FFFFFF]">
          <Link to="/" className="inline-flex items-center gap-2 text-sm hover:text-[#C4B088]">
            <ArrowLeft className="w-4 h-4" /> Back to website
          </Link>
          <div className="max-w-md">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#C4B088] mb-4">
              Operational Portal
            </p>
            <h1 className="font-serif text-5xl leading-[1.05] mb-5">
              Care that's <em className="not-italic text-[#C4B088]">organized</em>,
              compassionate, and on-time.
            </h1>
            <p className="text-[#FFFFFF]/75 leading-relaxed">
              One private dashboard for clients, caregivers, and our care team. Faster matching,
              clearer status, real peace of mind.
            </p>
            <ul className="mt-8 space-y-3 text-sm">
              {[
                { icon: HeartHandshake, t: "Submit & track care requests in minutes" },
                { icon: ShieldCheck, t: "Secure, role-based access for every team member" },
                { icon: Lock, t: "Private, encrypted, and HIPAA-mindful by design" },
              ].map((b, i) => (
                <li key={i} className="flex items-center gap-3 text-[#FFFFFF]/85">
                  <span className="w-9 h-9 rounded-full bg-[#FFFFFF]/10 border border-[#C4B088]/30 flex items-center justify-center">
                    <b.icon className="w-4 h-4 text-[#C4B088]" />
                  </span>
                  {b.t}
                </li>
              ))}
            </ul>
          </div>
          <p className="text-xs text-[#FFFFFF]/50">
            Madison Peaceful Path Home Care LLC · 608-513-7639
          </p>
        </div>
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center p-6 sm:p-12 min-h-screen">
        <div className="w-full max-w-md">
          <Link to="/" className="lg:hidden inline-flex items-center gap-2 text-sm text-[#64748B] mb-6">
            <ArrowLeft className="w-4 h-4" /> Back to website
          </Link>

          <Tabs value={mode} onValueChange={setMode}>
            <TabsList className="grid grid-cols-2 w-full mb-6 bg-[#F8F9FA] p-1 rounded-full">
              <TabsTrigger
                data-testid="auth-tab-login"
                value="login"
                className="rounded-full data-[state=active]:bg-[#005165] data-[state=active]:text-[#FFFFFF]"
              >
                Sign In
              </TabsTrigger>
              <TabsTrigger
                data-testid="auth-tab-register"
                value="register"
                className="rounded-full data-[state=active]:bg-[#005165] data-[state=active]:text-[#FFFFFF]"
              >
                Create Account
              </TabsTrigger>
            </TabsList>

            <form onSubmit={submit} data-testid="auth-form" className="space-y-4">
              {mode === "register" && (
                <>
                  <div>
                    <Label htmlFor="au-name">Full Name *</Label>
                    <Input
                      id="au-name"
                      data-testid="auth-name"
                      value={form.name}
                      onChange={(e) => update("name", e.target.value)}
                      className="mt-1.5 bg-white border-[#E5DDC9]"
                    />
                  </div>
                  <div>
                    <Label htmlFor="au-phone">Phone</Label>
                    <Input
                      id="au-phone"
                      data-testid="auth-phone"
                      value={form.phone}
                      onChange={(e) => update("phone", e.target.value)}
                      className="mt-1.5 bg-white border-[#E5DDC9]"
                    />
                  </div>
                  <div>
                    <Label htmlFor="au-role">I am a *</Label>
                    <Select value={form.role} onValueChange={(v) => update("role", v)}>
                      <SelectTrigger id="au-role" data-testid="auth-role" className="mt-1.5 bg-white border-[#E5DDC9]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="client">Family member seeking care</SelectItem>
                        <SelectItem value="caregiver">Caregiver / CNA / Med Tech</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}

              <div>
                <Label htmlFor="au-email">Email *</Label>
                <Input
                  id="au-email"
                  data-testid="auth-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  className="mt-1.5 bg-white border-[#E5DDC9]"
                />
              </div>
              <div>
                <Label htmlFor="au-pw">Password *</Label>
                <Input
                  id="au-pw"
                  data-testid="auth-password"
                  type="password"
                  value={form.password}
                  onChange={(e) => update("password", e.target.value)}
                  className="mt-1.5 bg-white border-[#E5DDC9]"
                />
              </div>

              <Button
                type="submit"
                data-testid="auth-submit"
                disabled={loading}
                className="w-full bg-[#005165] hover:bg-[#003946] text-[#FFFFFF] rounded-full h-12 btn-shimmer"
              >
                {loading ? "Please wait..." : mode === "login" ? "Sign In" : "Create Account"}
              </Button>

              <p className="text-xs text-center text-[#64748B] mt-4">
                {mode === "login" ? "Don't have an account? " : "Already have an account? "}
                <button
                  type="button"
                  onClick={() => setMode(mode === "login" ? "register" : "login")}
                  className="text-[#AC9667] hover:underline"
                >
                  {mode === "login" ? "Create one" : "Sign in"}
                </button>
              </p>
            </form>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
